<template>
  <section class="editor-screen">
    <div class="editor-shell">
      <div class="editor-header">
        <div>
          <button class="editor-back" @click="$emit('close')">返回我的层级</button>
          <span class="eyebrow">Layer Studio</span>
          <h2>编辑层级档案</h2>
        </div>
        <div class="editor-header-actions">
          <el-button class="profile-link-btn" @click="$emit('patrol')">巡逻层级</el-button>
          <el-button type="primary" :loading="editSaving || !!uploadingField || editorImageUploading" @click="handleSave">保存修改</el-button>
        </div>
      </div>

      <div class="editor-workspace">
        <section class="preview-pane">
          <div class="live-preview" :style="editPreviewStyle">
            <div class="preview-shade"></div>
            <div class="preview-content">
              <div class="brief-heading">
                <span>LIVE PREVIEW</span>
                <small>Level {{ editForm.levelNum || editForm.id }}</small>
              </div>
              <h2>{{ editForm.title || editForm.name || '未命名层级' }}</h2>
              <p>{{ editForm.description || '这里会实时展示你正在编辑的层级简介。' }}</p>
              <div class="preview-cover-card">
                <div>
                  <span class="eyebrow">Cover Image</span>
                  <strong>{{ editForm.img ? '封面图已载入' : '等待上传封面图' }}</strong>
                </div>
                <div class="preview-cover-img" :style="editCoverStyle">
                  <span v-if="!editCoverUrl">封面图</span>
                </div>
              </div>
              <div class="metrics preview-metrics">
                <div>
                  <span>状态</span>
                  <strong>{{ statusText(Number(editForm.status || 0)) }}</strong>
                </div>
                <div>
                  <span>访问</span>
                  <strong>{{ editForm.enterNum || 0 }}</strong>
                </div>
                <div>
                  <span>留言</span>
                  <strong>{{ editForm.messageNum || 0 }}</strong>
                </div>
                <div>
                  <span>收藏</span>
                  <strong>{{ editForm.favoriteNum || 0 }}</strong>
                </div>
                <div>
                  <span>上次巡逻</span>
                  <strong>{{ formatDate(editForm.guardedAt) }}</strong>
                </div>
              </div>
              <div class="patrol-strip editor-patrol-tip">
                <span>连续 {{ patrolExpireDays }} 天未巡逻后，该层级将自动变为无主层级。</span>
              </div>
              <div v-if="editForm.auditStatus === 1" class="audit-tip">当前修改正在等待管理员审核，通过后才会对其他用户可见。</div>
              <div v-if="editForm.auditStatus === 3" class="audit-tip reject">上次审核被驳回：{{ editForm.rejectReason || '请修改后重新提交。' }}</div>
              <div v-if="editForm.notice" class="notice">{{ editForm.notice }}</div>
              <section v-if="livePreviewHtml" class="level-html preview-html" v-html="livePreviewHtml"></section>
            </div>
          </div>
        </section>

        <aside class="editor-panel">
          <section class="editor-block">
            <div class="block-head">
              <span class="eyebrow">Media</span>
              <strong>本地图片上传</strong>
            </div>
            <div class="upload-grid">
              <button class="upload-card" type="button" @click="triggerImageSelect('backgroundImg')">
                <span>背景图</span>
                <strong>{{ editForm.backgroundImg ? '已选择' : '选择本地图片' }}</strong>
                <small>{{ uploadingField === 'backgroundImg' ? '上传中...' : '用于详情和实时预览背景' }}</small>
              </button>
              <button class="upload-card" type="button" @click="triggerImageSelect('img')">
                <span>封面图</span>
                <strong>{{ editForm.img ? '已选择' : '选择本地图片' }}</strong>
                <small>{{ uploadingField === 'img' ? '上传中...' : '用于档案卡片封面' }}</small>
              </button>
            </div>
            <input ref="backgroundInputRef" class="file-input" type="file" accept="image/*" @change="handleFileChange($event, 'backgroundImg')" />
            <input ref="coverInputRef" class="file-input" type="file" accept="image/*" @change="handleFileChange($event, 'img')" />
          </section>

          <el-form :model="editForm" label-position="top" class="edit-form studio-form">
            <section class="editor-block">
              <div class="block-head">
                <span class="eyebrow">Profile</span>
                <strong>基础信息</strong>
              </div>
              <div class="editor-help">提示：连续 {{ patrolExpireDays }} 天未巡逻，该层级会自动成为无主层级，其他用户可重新占领。</div>
              <div class="edit-grid">
                <el-form-item label="层级名称">
                  <el-input v-model="editForm.name" maxlength="20" />
                </el-form-item>
                <el-form-item label="展示标题">
                  <el-input v-model="editForm.title" maxlength="30" />
                </el-form-item>
              </div>
              <el-form-item label="简介">
                <el-input v-model="editForm.description" type="textarea" :rows="3" maxlength="120" show-word-limit />
              </el-form-item>
              <el-form-item label="层级公告">
                <el-input v-model="editForm.notice" type="textarea" :rows="2" maxlength="100" show-word-limit />
              </el-form-item>
            </section>

            <section class="editor-block">
              <div class="block-head">
                <span class="eyebrow">Content</span>
                <strong>富文本正文</strong>
              </div>
              <el-form-item label="内容">
                <div ref="richEditorRef" class="rich-editor">
                  <Toolbar class="editor-toolbar" :editor="editorRef" :defaultConfig="toolbarConfig" />
                  <Editor
                    class="editor-body"
                    v-model="editForm.html"
                    :defaultConfig="editorConfig"
                    mode="default"
                    @custom-alert="handleEditorAlert"
                    @onChange="handleEditorChange"
                    @onCreated="handleEditorCreated"
                  />
                </div>
              </el-form-item>
            </section>
          </el-form>
        </aside>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import '@wangeditor/editor/dist/css/style.css';
import { uploadImgBlob } from '@/api';
import type { ImageField, LevelItem } from './types';

const props = defineProps<{
  editForm: Partial<LevelItem>;
  editSaving: boolean;
  uploadingField: '' | ImageField;
  editPreviewStyle: Record<string, string>;
  editCoverStyle: Record<string, string>;
  editCoverUrl: string;
  patrolExpireDays: number;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
  (e: 'patrol'): void;
  (e: 'imageFile', field: ImageField, file: File): void;
}>();

const backgroundInputRef = ref<HTMLInputElement>();
const coverInputRef = ref<HTMLInputElement>();
const richEditorRef = ref<HTMLElement>();
const editorRef = shallowRef();
const editorImageUploading = ref(false);
const livePreviewHtml = ref('');
let imageResizeObserver: MutationObserver | null = null;
let previewSyncFrame = 0;
const toolbarConfig = {
  excludeKeys: ['group-video', 'insertVideo', 'uploadVideo'],
  modalAppendToBody: true
};
const editorConfig = {
  placeholder: '记录这个层级的环境、实体、入口、出口和生存规则...',
  MENU_CONF: {
    uploadImage: {
      allowedFileTypes: ['image/*'],
      maxFileSize: 8 * 1024 * 1024,
      customUpload: (file: File, insertFn: (src: string, alt: string, href: string) => void) => {
        if (!file.type.startsWith('image/')) {
          ElMessage.warning('请选择图片文件');
          return;
        }
        editorImageUploading.value = true;
        uploadImgBlob({
          url: 'file/wangeditor/uploadImg',
          file,
          data: { fileName: file.name },
          loading: { open: false, text: '' },
          success: (res: any) => {
            editorImageUploading.value = false;
            if (res.code !== 0) {
              ElMessage.error(res.msg || '正文图片上传失败');
              return;
            }
            const uploadedUrl = getUploadedImageUrl(res);
            if (!uploadedUrl) {
              ElMessage.warning('图片已上传，但未获取到图片地址');
              return;
            }
            insertFn(uploadedUrl, file.name, '');
            ElMessage.success('正文图片已插入');
          },
          error: () => {
            editorImageUploading.value = false;
            ElMessage.error('正文图片上传失败，请稍后重试');
          }
        });
      }
    }
  }
};

// 富文本图片走后端专用目录，兼容不同上传接口返回字段。
const getUploadedImageUrl = (res: any) => {
  const data = res?.data;
  if (typeof data === 'string') return data;
  return data?.url || data?.src || data?.path || '';
};

const triggerImageSelect = (field: ImageField) => {
  if (field === 'backgroundImg') backgroundInputRef.value?.click();
  else coverInputRef.value?.click();
};

const handleFileChange = (event: Event, field: ImageField) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (file) emit('imageFile', field, file);
};

const handleSave = () => {
  syncEditorWidthRatioHtmlFromEditor();
  emit('save');
};

const handleEditorCreated = (editor: any) => {
  editorRef.value = editor;
  editor.on('fullScreen', () => setEditorFullscreenState(true));
  editor.on('unFullScreen', () => setEditorFullscreenState(false));
  nextTick(() => {
    resetImageResizeObserver();
    syncLivePreviewFromEditor();
  });
};

const handleEditorChange = (editor: any) => {
  livePreviewHtml.value = getPreviewHtmlFromEditor(editor);
};

const getEditorRoot = () => {
  return richEditorRef.value?.querySelector<HTMLElement>('[data-slate-editor]') || null;
};

const resetImageResizeObserver = () => {
  imageResizeObserver?.disconnect();
  imageResizeObserver = null;
  const editorRoot = getEditorRoot();
  if (!editorRoot || typeof MutationObserver === 'undefined') return;

  imageResizeObserver = new MutationObserver((mutations) => {
    const shouldSync = mutations.some((mutation) => {
      if (mutation.type === 'childList') return true;
      const target = mutation.target as HTMLElement;
      return target.classList?.contains('w-e-image-container') && mutation.attributeName === 'style';
    });
    if (shouldSync) scheduleLivePreviewSync();
  });

  imageResizeObserver.observe(editorRoot, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['style']
  });
};

const scheduleLivePreviewSync = () => {
  if (typeof window === 'undefined') return;
  if (previewSyncFrame) window.cancelAnimationFrame(previewSyncFrame);
  previewSyncFrame = window.requestAnimationFrame(() => {
    previewSyncFrame = 0;
    syncLivePreviewFromEditor();
  });
};

const syncLivePreviewFromEditor = () => {
  livePreviewHtml.value = getPreviewHtmlFromEditor(editorRef.value);
};

const syncEditorWidthRatioHtmlFromEditor = () => {
  const ratioHtml = getPreviewHtmlFromEditor(editorRef.value);
  props.editForm.html = ratioHtml;
  livePreviewHtml.value = ratioHtml;
};

type EditorImageWidthRatio = {
  src: string;
  alt: string;
  href: string;
  width: string;
};

const getEditorImageWidthRatios = (): EditorImageWidthRatio[] => {
  const editorRoot = getEditorRoot();
  if (!editorRoot) return [];
  const containers = Array.from(editorRoot.querySelectorAll<HTMLElement>('.w-e-image-container'));

  return containers
    .map((container) => {
      const image = container.querySelector<HTMLImageElement>('img');
      if (!image) return null;
      return {
        src: image.getAttribute('src') || '',
        alt: image.getAttribute('alt') || '',
        href: image.getAttribute('data-href') || '',
        width: getImageWidthRatioStyle(container)
      };
    })
    .filter((item): item is EditorImageWidthRatio => !!item && !!item.src);
};

// 预览图片按“图片在富文本正文中占了多少宽度”映射，不直接照搬 px。
const getImageWidthRatioStyle = (container: HTMLElement) => {
  const styleWidth = container.style.width.trim();
  if (styleWidth.endsWith('%')) return styleWidth;

  const editorWidth = getEditorContentWidth();
  const imageWidth = container.getBoundingClientRect().width;
  if (!editorWidth || !imageWidth) return styleWidth;

  const percent = Math.max(1, Math.min(100, (imageWidth / editorWidth) * 100));
  return `${formatPercent(percent)}%`;
};

const getEditorContentWidth = () => {
  const editorRoot = getEditorRoot();
  if (!editorRoot) return 0;
  const style = window.getComputedStyle(editorRoot);
  const paddingX = parseFloat(style.paddingLeft || '0') + parseFloat(style.paddingRight || '0');
  return Math.max(0, editorRoot.clientWidth - paddingX);
};

const formatPercent = (value: number) => {
  return value.toFixed(2).replace(/\.?0+$/, '');
};

const getPreviewHtmlFromEditor = (editor: any) => {
  const html = editor?.getHtml?.() || props.editForm.html || '';
  const htmlWithTables = restoreEditorTablesIfNeeded(html);
  return applyEditorImageWidthRatioToHtml(htmlWithTables);
};

const restoreEditorTablesIfNeeded = (html: string) => {
  const editorRoot = getEditorRoot();
  if (!editorRoot?.querySelector('table')) return html;

  const htmlTableCount = (html.match(/<table\b/gi) || []).length;
  const editorTableCount = editorRoot.querySelectorAll('table').length;
  if (htmlTableCount >= editorTableCount) return html;

  return serializeEditorDomForPreview() || html;
};

const serializeEditorDomForPreview = () => {
  const editorRoot = getEditorRoot();
  if (!editorRoot) return '';

  const clone = editorRoot.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.w-e-image-dragger, [data-slate-zero-width]').forEach((element) => element.remove());
  clone.querySelectorAll('.table-container').forEach((container) => {
    const table = container.querySelector('table');
    if (table) container.replaceWith(table);
  });

  cleanEditorPreviewNode(clone);
  unwrapPlainSpans(clone);
  return clone.innerHTML;
};

const cleanEditorPreviewNode = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (attribute.name.startsWith('data-slate') || attribute.name === 'contenteditable' || attribute.name === 'spellcheck') {
        element.removeAttribute(attribute.name);
      }
    });
  });
};

const unwrapPlainSpans = (root: HTMLElement) => {
  Array.from(root.querySelectorAll('span')).forEach((span) => {
    if (span.attributes.length > 0) return;
    span.replaceWith(...Array.from(span.childNodes));
  });
};

const applyEditorImageWidthRatioToHtml = (html: string) => {
  if (!html) return '';
  const editorImages = getEditorImageWidthRatios();
  if (editorImages.length === 0 && !/<table/i.test(html)) return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  applyTablePreviewStyles(template.content);
  const previewImages = Array.from(template.content.querySelectorAll<HTMLImageElement>('img'));
  const usedIndexes = new Set<number>();

  previewImages.forEach((image, imageIndex) => {
    const matchedIndex = findEditorImageIndex(image, editorImages, usedIndexes, imageIndex);
    if (matchedIndex < 0) return;
    const matchedImage = editorImages[matchedIndex];
    usedIndexes.add(matchedIndex);

    const imageContainer = image.closest('.w-e-image-container') as HTMLElement | null;
    const target = imageContainer || image;
    if (matchedImage.width) target.style.width = matchedImage.width;
    else target.style.removeProperty('width');

    target.style.removeProperty('height');
    if (imageContainer) {
      image.style.width = '100%';
      image.style.height = 'auto';
    } else {
      image.style.removeProperty('height');
    }
  });

  return template.innerHTML;
};

const applyTablePreviewStyles = (root: DocumentFragment) => {
  root.querySelectorAll<HTMLTableElement>('table').forEach((table) => {
    mergeInlineStyles(table, {
      maxWidth: '100%',
      margin: '14px 0',
      borderCollapse: 'collapse',
      border: '1px solid rgba(0, 240, 216, 0.34)',
      background: 'rgba(5, 10, 22, 0.36)'
    });
  });

  root.querySelectorAll<HTMLTableCellElement>('th, td').forEach((cell) => {
    mergeInlineStyles(cell, {
      minWidth: '44px',
      padding: '8px 10px',
      border: '1px solid rgba(0, 240, 216, 0.34)',
      lineHeight: '1.55',
      textAlign: 'left',
      verticalAlign: 'top'
    });

    if (!cell.textContent?.trim() && cell.children.length === 0) {
      cell.innerHTML = '&nbsp;';
    }
  });

  root.querySelectorAll<HTMLTableCellElement>('th').forEach((cell) => {
    mergeInlineStyles(cell, {
      color: '#dffefa',
      fontWeight: '800',
      background: 'rgba(0, 240, 216, 0.12)'
    });
  });
};

const mergeInlineStyles = (element: HTMLElement, styles: Partial<CSSStyleDeclaration>) => {
  Object.entries(styles).forEach(([key, value]) => {
    if (value) element.style[key as any] = value;
  });
};

const findEditorImageIndex = (image: HTMLImageElement, editorImages: EditorImageWidthRatio[], usedIndexes: Set<number>, fallbackIndex: number) => {
  const src = image.getAttribute('src') || '';
  const alt = image.getAttribute('alt') || '';
  const href = image.getAttribute('data-href') || '';
  const matchedIndex = editorImages.findIndex((item, index) => {
    return !usedIndexes.has(index) && item.src === src && item.alt === alt && item.href === href;
  });

  if (matchedIndex >= 0) return matchedIndex;
  if (!usedIndexes.has(fallbackIndex) && editorImages[fallbackIndex]) return fallbackIndex;
  return editorImages.findIndex((_, index) => !usedIndexes.has(index));
};

const handleEditorAlert = (info: string, type: 'success' | 'info' | 'warning' | 'error') => {
  ElMessage[type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'info'](info);
};

const setEditorFullscreenState = (value: boolean) => {
  document.body.classList.toggle('level-editor-fullscreen-active', value);
};

const destroyEditor = () => {
  imageResizeObserver?.disconnect();
  imageResizeObserver = null;
  if (previewSyncFrame) {
    window.cancelAnimationFrame(previewSyncFrame);
    previewSyncFrame = 0;
  }
  if (editorRef.value) {
    editorRef.value.destroy();
    editorRef.value = null;
  }
};

const statusText = (status: number) => {
  if (status === 1) return '已占领';
  if (status === 2) return '待上线';
  if (status === -1) return '已删除';
  return '未占领';
};

const formatDate = (value?: string) => {
  if (!value) return '暂无';
  return String(value).replace('T', ' ').slice(0, 19);
};

watch(
  () => props.editForm.html,
  (html) => {
    livePreviewHtml.value = html || '';
    nextTick(() => {
      resetImageResizeObserver();
      syncLivePreviewFromEditor();
    });
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  setEditorFullscreenState(false);
  destroyEditor();
});
</script>
